import { useEffect, useRef, useState } from 'react';
import type { TableInfo } from '@/types/api';
import { Plus, Unlink, Trash2 } from 'lucide-react';

export interface TableCanvasPopupProps {
  /** Canvas-relative position (same coordinate space as table x/y). */
  x: number;
  y: number;
  mode: 'empty' | 'table';
  unassignedTables: TableInfo[];
  loadingUnassigned?: boolean;
  onCreateNew: (name: string, capacity: number) => void;
  onAddUnassigned: (table: TableInfo) => void;
  onDetach: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function TableCanvasPopup({
  x,
  y,
  mode,
  unassignedTables,
  loadingUnassigned,
  onCreateNew,
  onAddUnassigned,
  onDetach,
  onDelete,
  onClose,
}: TableCanvasPopupProps) {
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('4');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const cap = parseInt(capacity, 10);
    onCreateNew(trimmed, Number.isFinite(cap) && cap > 0 ? cap : 4);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this table? This removes it everywhere, including the POS.')) {
      onDelete();
    }
  };

  return (
    <div
      ref={ref}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 300,
      }}
      className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[220px] flex flex-col gap-2"
    >
      {mode === 'table' ? (
        <>
          <button
            onClick={onDetach}
            className="flex items-center gap-2 px-2 py-1.5 rounded text-sm w-full text-left hover:bg-gray-100 text-gray-700"
          >
            <Unlink className="h-4 w-4" />
            Detach from Floor
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-2 py-1.5 rounded text-sm w-full text-left hover:bg-red-50 text-red-600"
          >
            <Trash2 className="h-4 w-4" />
            Delete Table
          </button>
        </>
      ) : (
        <>
          {loadingUnassigned && (
            <div className="text-xs text-gray-400 px-1">Loading unassigned tables…</div>
          )}
          {!loadingUnassigned && unassignedTables.length > 0 && (
            <div className="flex flex-col gap-0.5 max-h-40 overflow-auto">
              <div className="text-xs font-semibold text-gray-500 px-1 pb-1">Unassigned tables</div>
              {unassignedTables.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onAddUnassigned(t)}
                  className="flex items-center justify-between px-2 py-1.5 rounded text-sm w-full text-left hover:bg-gray-100 text-gray-700"
                >
                  <span>{t.tableNumber}</span>
                  <span className="text-xs text-gray-400">{t.capacity} seats</span>
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-gray-100 pt-2 flex flex-col gap-1.5">
            <div className="text-xs font-semibold text-gray-500 px-1">Create new table</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Table name"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm h-8 px-2"
            />
            <input
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              type="number"
              min={1}
              placeholder="Capacity"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm h-8 px-2"
            />
            <button
              onClick={handleCreate}
              disabled={!name.trim()}
              className="btn btn-primary h-8 text-sm justify-center"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Create Table
            </button>
          </div>
        </>
      )}
    </div>
  );
}
